/**
 * CREATIVE DETONATOR - Generator State
 * Phase 2: Store Facade Implementation
 *
 * MIGRATION: This module now uses the central Store as source of truth.
 * The GeneratorState object is a FACADE that reads/writes through Store.
 * Legacy code using GeneratorState continues to work during migration.
 *
 * @deprecated Direct GeneratorState access is deprecated.
 * Use Store.get('generator.*') and Store.dispatch() instead.
 *
 * Note: TriggerImageMap is imported from window.TriggerImageFiles (defined in main.js)
 * to maintain a Single Source of Truth for image filename mappings.
 * FRONTEND-25: Uses Logger for environment-aware logging
 */

import { API_BASE, ENDPOINTS } from '../../../js/api/endpoints.js';
import { apiJson } from '../../../js/api/client.js';
import { createLogger } from '../../../js/core/logger.js';
import {
  initActions as initGeneratorActions,
  setPhase as actionSetPhase,
  setBrief as actionSetBrief,
  updateBrief as actionUpdateBrief,
  setFormats as actionSetFormats,
  addFormat as actionAddFormat,
  removeFormat as actionRemoveFormat,
  toggleFormat as actionToggleFormat,
  setTriggers as actionSetTriggers,
  addTrigger as actionAddTrigger,
  removeTrigger as actionRemoveTrigger,
  toggleTrigger as actionToggleTrigger,
  setIdeas as actionSetIdeas,
  addIdea as actionAddIdea,
  updateIdea as actionUpdateIdea,
  removeIdea as actionRemoveIdea,
  setCurrentIdea as actionSetCurrentIdea,
  setGenerating as actionSetGenerating,
  setGenerationProgress as actionSetGenerationProgress,
  setBrand as actionSetBrand,
  setSessionId as actionSetSessionId,
  setLoading as actionSetLoading,
  setError as actionSetError,
  resetGenerator as actionResetGenerator
} from '../../../js/store/actions/generator.js';

// FRONTEND-25: Namespaced logger for generator state
const log = createLogger('generator');

// ============================================================================
// DEPRECATION HELPER
// ============================================================================

const warnedProperties = new Set();

function warnDeprecation(property) {
  if (import.meta.env?.DEV && !warnedProperties.has(property)) {
    console.warn(`[DEPRECATED] GeneratorState.${property} - Use Store.get('generator.*') or dispatch actions`);
    warnedProperties.add(property);
  }
}

// ============================================================================
// STORE ACCESS HELPER
// ============================================================================

function getStore() {
  const store = window.Store;
  if (!store) {
    console.warn('[GeneratorState] window.Store not available!');
  }
  return store;
}

// ============================================================================
// API CONFIGURATION
// ============================================================================

// Note: API_BASE is now imported from endpoints.js (Single Source of Truth)
export const API_CONFIG = {
  baseUrl: API_BASE,
  endpoints: {
    generate: ENDPOINTS.generate,
    status: ENDPOINTS.status,
    exportSession: ENDPOINTS.media.exportSession
  }
};

// ============================================================================
// GENERATOR STATE FACADE
// Phase 2: All properties read/write through Store
// ============================================================================

/**
 * Internal state for properties not yet in Store
 * (These will be migrated to Store in future iterations)
 */
const internalState = {
  // Selection state (local to generator session)
  selectedIdeas: [],  // Visual selection (click card)
  savedIdeas: [],     // Saved for export (click SAVE button)

  // Config flags
  simulationMode: false,
  ideasCount: 3,
  streamingEnabled: true,

  // Session data from backend
  session: null,
  promptVersion: null,

  // User state (synced from auth)
  user: {
    id: null,
    email: null,
    displayName: null,
    credits: 0,
    isAuthenticated: false,
    isAdmin: false
  },

  // V2 phase progress (for UI)
  v2Progress: {
    currentPhase: null,
    phase1: { status: 'pending', fragmentsGenerated: 0 },
    phase2: { status: 'pending', fragmentsSelected: 0 },
    phase3: { status: 'pending', formatsCompleted: 0 }
  },

  // Image generation config (loaded from backend)
  imageConfig: {
    autoGeneration: false,
    autoProvider: 'seedream',
    autoAspect: '4:3',
    maxConcurrent: 3,
    autoStyle: 'auto',
    maxRetries: 2,
    autoVideoStyle: null
  },

  // Pre-loaded image URLs
  preloadedImages: {},

  // Pre-loaded video URLs
  preloadedVideos: {},

  // Task 102: Track which media (image/video) was generated most recently per idea
  // Used to determine display priority when both exist
  latestMediaTimestamp: {},

  // Refinement state
  refinedIdeas: {},
  refiningIdeaId: null,

  // Brand state (additional to Store)
  sessionBrand: null,
  sessionProduct: null,
  selectedProduct: null
};

/**
 * GeneratorState Facade
 * Provides backwards compatible API while reading/writing through Store
 */
export const GeneratorState = {
  // ==========================================================================
  // STORE-BACKED PROPERTIES (read from Store, write via actions)
  // ==========================================================================

  get phase() {
    return getStore()?.get('generator.phase') ?? 1;
  },
  set phase(value) {
    warnDeprecation('phase = value');
    actionSetPhase(value);
  },

  get brief() {
    const defaultBrief = { product: '', benefit: '', audience: '', tone: '', formats: [], productId: null, productData: null };
    const storeBrief = getStore()?.get('generator.brief');
    // Use generator.formats as canonical source (actions write there)
    const formats = getStore()?.get('generator.formats') ?? [];
    // Merge with defaults to ensure all expected fields exist
    return storeBrief
      ? { ...defaultBrief, ...storeBrief, formats }
      : { ...defaultBrief, formats };
  },
  set brief(value) {
    warnDeprecation('brief = value');
    actionSetBrief(value);
  },

  // Legacy 'selectedTriggers' maps to 'generator.triggers' in Store
  get selectedTriggers() {
    return getStore()?.get('generator.triggers') ?? [];
  },
  set selectedTriggers(value) {
    warnDeprecation('selectedTriggers = value');
    actionSetTriggers(value);
  },

  // Legacy 'generatedIdeas' maps to 'generator.ideas' in Store
  get generatedIdeas() {
    return getStore()?.get('generator.ideas') ?? [];
  },
  set generatedIdeas(value) {
    warnDeprecation('generatedIdeas = value');
    actionSetIdeas(value);
  },

  get isGenerating() {
    return getStore()?.get('generator.isGenerating') ?? false;
  },
  set isGenerating(value) {
    warnDeprecation('isGenerating = value');
    actionSetGenerating(value);
  },

  get currentBrand() {
    return getStore()?.get('generator.brand') ?? null;
  },
  set currentBrand(value) {
    warnDeprecation('currentBrand = value');
    actionSetBrand(value);
  },

  // ==========================================================================
  // INTERNAL STATE PROPERTIES (not yet in Store)
  // ==========================================================================

  get selectedIdeas() { return internalState.selectedIdeas; },
  set selectedIdeas(value) { internalState.selectedIdeas = value; },

  get savedIdeas() { return internalState.savedIdeas; },
  set savedIdeas(value) { internalState.savedIdeas = value; },

  get simulationMode() { return internalState.simulationMode; },
  set simulationMode(value) { internalState.simulationMode = value; },

  get ideasCount() { return internalState.ideasCount; },
  set ideasCount(value) { internalState.ideasCount = value; },

  get session() { return internalState.session; },
  set session(value) { internalState.session = value; },

  get promptVersion() { return internalState.promptVersion; },
  set promptVersion(value) { internalState.promptVersion = value; },

  get user() { return internalState.user; },
  set user(value) { internalState.user = value; },

  get v2Progress() { return internalState.v2Progress; },
  set v2Progress(value) { internalState.v2Progress = value; },

  get imageConfig() { return internalState.imageConfig; },
  set imageConfig(value) { internalState.imageConfig = value; },

  get streamingEnabled() { return internalState.streamingEnabled; },
  set streamingEnabled(value) { internalState.streamingEnabled = value; },

  get preloadedImages() { return internalState.preloadedImages; },
  set preloadedImages(value) { internalState.preloadedImages = value; },

  get preloadedVideos() { return internalState.preloadedVideos; },
  set preloadedVideos(value) { internalState.preloadedVideos = value; },

  // Task 102: Track latest media type per idea for display priority
  get latestMediaTimestamp() { return internalState.latestMediaTimestamp; },
  set latestMediaTimestamp(value) { internalState.latestMediaTimestamp = value; },

  get refinedIdeas() { return internalState.refinedIdeas; },
  set refinedIdeas(value) { internalState.refinedIdeas = value; },

  get refiningIdeaId() { return internalState.refiningIdeaId; },
  set refiningIdeaId(value) { internalState.refiningIdeaId = value; },

  get sessionBrand() { return internalState.sessionBrand; },
  set sessionBrand(value) { internalState.sessionBrand = value; },

  get sessionProduct() { return internalState.sessionProduct; },
  set sessionProduct(value) { internalState.sessionProduct = value; },

  get selectedProduct() { return internalState.selectedProduct; },
  set selectedProduct(value) { internalState.selectedProduct = value; }
};

// ============================================================================
// DOM ELEMENTS CACHE
// ============================================================================

// DOM Elements (populated by cacheElements in generator.js)
export let elements = {};

/**
 * Set the cached DOM elements
 * Called by generator.js during DOMContentLoaded
 * @param {Object} els - The cached DOM elements object
 */
export function setElements(els) {
  // MUST mutate, not reassign - ES6 imports are live bindings
  Object.assign(elements, els);
}

// ============================================================================
// SESSION HELPERS
// ============================================================================

/**
 * Store session data from API response
 * @param {Object} session - Session data from backend
 */
export function setSession(session) {
  internalState.session = session;
  internalState.promptVersion = session?.version || 'v1';
  // Also set session ID in Store
  if (session?.id) {
    actionSetSessionId(session.id);
  }
}

/**
 * Get session data for export
 * @returns {Object|null} - Session data or null
 */
export function getSession() {
  return internalState.session;
}

/**
 * Reset V2 progress state
 */
export function resetV2Progress() {
  internalState.v2Progress = {
    currentPhase: null,
    phase1: { status: 'pending', fragmentsGenerated: 0 },
    phase2: { status: 'pending', fragmentsSelected: 0 },
    phase3: { status: 'pending', formatsCompleted: 0 }
  };
}

/**
 * Update V2 phase progress
 * @param {number} phase - Phase number (1, 2, or 3)
 * @param {string} status - Status ('in_progress', 'completed', 'error')
 * @param {Object} data - Optional data (fragmentsGenerated, etc.)
 */
export function updateV2Progress(phase, status, data = {}) {
  internalState.v2Progress.currentPhase = phase;

  if (phase === 1) {
    internalState.v2Progress.phase1 = { status, ...data };
  } else if (phase === 2) {
    internalState.v2Progress.phase2 = { status, ...data };
  } else if (phase === 3) {
    internalState.v2Progress.phase3 = { status, ...data };
  }
}

// ============================================================================
// IMAGE CONFIG LOADING
// ============================================================================

/**
 * Load config from backend (image generation + streaming)
 * Call this early (before detonation) to know what features are available
 * Uses authenticated request to load user-specific preferences
 */
export async function loadImageConfig() {
  try {
    log.debug('Fetching config from:', '/api/config');

    // Use apiJson to include auth token for user-specific preferences
    const data = await apiJson('/api/config');
    log.debug('Config response:', data);

    if (data.success && data.config) {
      // Image generation config
      internalState.imageConfig = {
        autoGeneration: data.config.autoImageGeneration === true,
        autoProvider: data.config.autoImageProvider || 'seedream',
        autoAspect: data.config.autoImageAspect || '4:3',
        maxConcurrent: data.config.autoImageMaxConcurrent || 3,
        autoStyle: data.config.autoImageStyle || 'auto',
        maxRetries: data.config.autoImageMaxRetries ?? 2,
        autoFormats: data.config.autoImageFormats || ['print'],
        autoVideoStyle: data.config.autoVideoStyle || null
      };

      // Streaming config
      internalState.streamingEnabled = data.config.streamingEnabled === true;

      log.info('Config loaded:', {
        autoGeneration: internalState.imageConfig.autoGeneration,
        streaming: internalState.streamingEnabled,
        provider: internalState.imageConfig.autoProvider
      });
    } else {
      log.warn('Invalid config response:', data);
    }
  } catch (error) {
    log.error('Failed to load config:', error);
  }
}

/**
 * Reset preloaded images (called at start of new generation)
 */
export function resetPreloadedImages() {
  internalState.preloadedImages = {};
}

// ============================================================================
// REFINEMENT HELPERS
// ============================================================================

/**
 * Store a refined idea (replaces in-place)
 * @param {string} ideaId - The original idea ID
 * @param {Object} refinedIdea - The refined idea object
 */
export function setRefinedIdea(ideaId, refinedIdea) {
  internalState.refinedIdeas[ideaId] = refinedIdea;

  // Also update in Store ideas array for consistency
  const ideas = getStore()?.get('generator.ideas') || [];
  for (const group of ideas) {
    if (group.ideas) {
      const index = group.ideas.findIndex(i => i.id === ideaId);
      if (index !== -1) {
        group.ideas[index] = refinedIdea;
        actionSetIdeas([...ideas]);
        break;
      }
    }
  }
}

/**
 * Get the current version of an idea (refined or original)
 * @param {string} ideaId - The idea ID
 * @returns {Object|null} - The idea object
 */
export function getCurrentIdea(ideaId) {
  // Check refinedIdeas first
  if (internalState.refinedIdeas[ideaId]) {
    return internalState.refinedIdeas[ideaId];
  }

  // Fall back to original in Store ideas
  const ideas = getStore()?.get('generator.ideas') || [];
  for (const group of ideas) {
    if (group.ideas) {
      const idea = group.ideas.find(i => i.id === ideaId);
      if (idea) return idea;
    }
  }

  return null;
}

/**
 * Set refining state
 * @param {string|null} ideaId - ID being refined, or null if done
 */
export function setRefiningIdea(ideaId) {
  internalState.refiningIdeaId = ideaId;
}

/**
 * Check if an idea is currently being refined
 * @param {string} ideaId - The idea ID
 * @returns {boolean}
 */
export function isRefining(ideaId) {
  return internalState.refiningIdeaId === ideaId;
}

/**
 * Reset refinement state (called at start of new generation)
 */
export function resetRefinementState() {
  internalState.refinedIdeas = {};
  internalState.refiningIdeaId = null;
}

// ============================================================================
// BRAND HELPERS (V5)
// ============================================================================

/**
 * Set the current brand for generation
 * Called when brand is detected from localStorage on generator init
 *
 * IMPORTANT: This clears selectedProduct to prevent stale product data from
 * a previous brand being used for image generation prompts.
 *
 * @param {Object|null} brand - Brand object or null to clear
 */
export function setBrand(brand) {
  const previousBrand = getStore()?.get('generator.brand');

  // CRITICAL: Clear product data when brand changes to prevent stale data
  if (previousBrand?.id !== brand?.id) {
    internalState.selectedProduct = null;
    const brief = getStore()?.get('generator.brief') || {};
    actionSetBrief({ ...brief, productId: null, productData: null });
    log.debug('Brand changed - cleared stale product data');
  }

  // Set brand in Store
  actionSetBrand(brand);

  if (brand) {
    // Auto-fill product field with brand name if empty
    const brief = getStore()?.get('generator.brief') || {};
    if (!brief.product) {
      actionUpdateBrief({ product: brand.name });
    }
    log.debug(`Brand set: ${brand.name}`);
  }
}

/**
 * Clear the current brand
 * Also clears any selected product data to prevent stale data
 */
export function clearBrand() {
  log.debug('Brand cleared');
  actionSetBrand(null);
  internalState.selectedProduct = null;
  const brief = getStore()?.get('generator.brief') || {};
  actionSetBrief({ ...brief, productId: null, productData: null });
}

/**
 * Get the current brand ID for API calls
 * @returns {string|null} - Brand UUID or null
 */
export function getBrandId() {
  return getStore()?.get('generator.brand')?.id || null;
}

/**
 * Check if a brand is currently loaded
 * @returns {boolean}
 */
export function hasBrand() {
  return getStore()?.get('generator.brand') !== null;
}

/**
 * Reset brand state (called at start of new session if needed)
 */
export function resetBrandState() {
  actionSetBrand(null);
}

// ============================================================================
// USER/AUTH HELPERS
// ============================================================================

/**
 * Set user data from auth state
 * Called by auth module when user logs in or state changes
 * @param {Object} user - User data from auth state
 * @param {Object} profile - Profile data from backend
 */
export function setUser(user, profile) {
  if (user && profile) {
    internalState.user = {
      id: user.id,
      email: user.email,
      displayName: profile.display_name || user.email?.split('@')[0] || 'User',
      credits: profile.credits || 0,
      isAuthenticated: true,
      isAdmin: profile.role === 'admin'
    };
  } else {
    internalState.user = {
      id: null,
      email: null,
      displayName: null,
      credits: 0,
      isAuthenticated: false,
      isAdmin: false
    };
  }
}

/**
 * Update user credits (after generation, purchase, etc.)
 * @param {number} credits - New credit balance
 */
export function updateUserCredits(credits) {
  internalState.user.credits = credits;
}

/**
 * Check if user has sufficient credits for an operation
 * @param {number} required - Credits required
 * @returns {boolean}
 */
export function hasCredits(required) {
  return internalState.user.credits >= required;
}

/**
 * Calculate credits needed for current generation
 * Credits = triggers * formats
 * @returns {number}
 */
export function getCreditsNeeded() {
  const triggers = (getStore()?.get('generator.triggers') || []).length;
  const brief = getStore()?.get('generator.brief') || {};
  const formats = brief.formats?.length || 0;
  return triggers * formats;
}

// ============================================================================
// TRIGGER IMAGE MAPPING
// ============================================================================

// Use global from main.js to maintain Single Source of Truth (SSOT)
// TriggerImageFiles is defined in main.js and loaded before this module
export const TriggerImageMap = window.TriggerImageFiles;

// ============================================================================
// STORE SYNCHRONIZATION (Phase 2)
// ============================================================================

/**
 * Sync internal state to Store (for properties not yet Store-backed)
 * This is called after any local state change
 */
export function syncToStore() {
  if (!getStore()) return;

  // Sync Store-backed properties are already automatic
  // This function can be used for additional sync if needed
  log.debug('State synced to Store');
}

/**
 * Subscribe to Store changes and sync back to internal state
 * (for bidirectional sync during migration)
 */
export function subscribeToStore() {
  if (!getStore()) return;

  getStore().subscribe('generator', (newState, _, path) => {
    // Store changes are automatically reflected via getters
    log.debug('Store change detected:', path);
  });
}

/**
 * Initialize Store connection
 * Phase 2: Initializes action modules for dispatch pattern
 */
export function initStoreSync() {
  if (!getStore()) {
    log.warn('Store not available for sync');
    return;
  }

  // Phase 2: Initialize generator actions module
  initGeneratorActions();

  // Subscribe to Store changes
  subscribeToStore();

  log.debug('Generator state connected to Store with action modules');
}

// ============================================================================
// SESSION PERSISTENCE (Fix for page refresh losing ideas)
// ============================================================================

const SESSION_STATE_KEY = 'cd_generator_session_state';

/**
 * Persist critical GeneratorState to sessionStorage
 * Call this after ideas are generated or modified
 * Enables recovery after page refresh in Phase 4
 */
export function persistSessionState() {
  try {
    const stateToSave = {
      phase: getStore()?.get('generator.phase') || 1,
      brief: getStore()?.get('generator.brief') || {},
      selectedTriggers: getStore()?.get('generator.triggers') || [],
      generatedIdeas: getStore()?.get('generator.ideas') || [],
      session: internalState.session,
      currentBrand: getStore()?.get('generator.brand') ? {
        id: getStore().get('generator.brand').id,
        name: getStore().get('generator.brand').name
      } : null,
      selectedProduct: internalState.selectedProduct,
      imageConfig: internalState.imageConfig,
      // Task 102: Persist latest media timestamps for display priority after refresh
      latestMediaTimestamp: internalState.latestMediaTimestamp,
      timestamp: Date.now()
    };

    sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(stateToSave));
    log.debug('Session state persisted');
  } catch (e) {
    log.warn('Failed to persist session state:', e.message);
  }
}

/**
 * Restore GeneratorState from sessionStorage
 * Call this on page load before checking URL params
 * @returns {boolean} True if state was restored
 */
export function restoreSessionState() {
  try {
    const stored = sessionStorage.getItem(SESSION_STATE_KEY);
    if (!stored) return false;

    const savedState = JSON.parse(stored);

    // Check if state is stale (older than 30 minutes)
    const MAX_AGE = 30 * 60 * 1000;
    if (Date.now() - savedState.timestamp > MAX_AGE) {
      log.debug('Session state expired, clearing');
      sessionStorage.removeItem(SESSION_STATE_KEY);
      return false;
    }

    // Only restore if we have generated ideas (main purpose of this)
    if (!savedState.generatedIdeas || savedState.generatedIdeas.length === 0) {
      return false;
    }

    // Restore state via Store actions
    actionSetPhase(savedState.phase || 1);
    actionSetBrief(savedState.brief || { product: '', benefit: '', formats: [] });
    actionSetTriggers(savedState.selectedTriggers || []);
    actionSetIdeas(savedState.generatedIdeas || []);

    internalState.session = savedState.session || null;
    internalState.selectedProduct = savedState.selectedProduct || null;
    internalState.imageConfig = savedState.imageConfig || internalState.imageConfig;

    // Restore preloadedImages and preloadedVideos from idea data
    internalState.preloadedImages = {};
    internalState.preloadedVideos = {};
    for (const group of savedState.generatedIdeas || []) {
      if (group.ideas) {
        for (const idea of group.ideas) {
          const imageUrl = idea.imageUrl || idea.image_url;
          if (imageUrl) {
            internalState.preloadedImages[idea.id] = imageUrl;
          }
          const videoUrl = idea.videoUrl || idea.video_url;
          if (videoUrl) {
            internalState.preloadedVideos[idea.id] = videoUrl;
          }
        }
      }
    }

    // Task 102: Restore latest media timestamps for display priority
    internalState.latestMediaTimestamp = savedState.latestMediaTimestamp || {};

    log.info('Session state restored:', {
      phase: savedState.phase,
      ideas: (savedState.generatedIdeas || []).reduce((sum, g) => sum + (g.ideas?.length || 0), 0)
    });

    return true;
  } catch (e) {
    log.warn('Failed to restore session state:', e.message);
    sessionStorage.removeItem(SESSION_STATE_KEY);
    return false;
  }
}

/**
 * Clear persisted session state
 * Call when starting a new generation or navigating away
 */
export function clearSessionState() {
  sessionStorage.removeItem(SESSION_STATE_KEY);
  log.debug('Session state cleared');
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for compatibility during migration and for non-module scripts
window.GeneratorState = GeneratorState;
window.API_CONFIG = API_CONFIG;
window.TriggerImageMap = TriggerImageMap;

// Log migration status in development
if (import.meta.env?.DEV) {
  console.info('[GeneratorState] Phase 2: Now backed by central Store. Direct access deprecated.');
}
